/**
 * clinicalConfigService.js
 * Servicio unificado de administración clínica para WE360.
 * Consolida el control de umbrales basales, reglas OMS y programas multi-patología.
 */

import { db } from "@/firebase";
import { doc, getDoc, setDoc, deleteDoc, collection, getDocs } from "firebase/firestore";

// ── 1. Umbrales generales por defecto (basados en guías internacionales) ────────
export const DEFAULT_THRESHOLDS = {
  paSistolica:  { info: 120, warning: 130, danger: 140, critical: 180 },
  paDiastolica: { warning: 80, danger: 90, critical: 120 },
  glucemia:     { hypoCritical: 54, hypo: 70, warning: 100, danger: 126, severe: 250 },
  oximetria:    { warning: 95, danger: 92, critical: 90 },
  stressIndex:  { info: 200, warning: 350, danger: 500 }, // Baevsky (0-1000)
  imc:          { warning: 25, danger: 30, critical: 40 },
  sueno:        { warning: 7 },
};

// ── 2. Reglas de monitoreo cardiovascular de la OMS por defecto ───────────────
export const DEFAULT_OMS_THRESHOLDS = {
  rules: {
    age: { enabled: true, value: 40, label: "Edad del usuario", unit: "años", description: "Rango de edad donde se incrementa la vulnerabilidad arterial según la OMS." },
    systolic: { enabled: true, value: 140, label: "Presión Arterial Sistólica (PAS)", unit: "mmHg", description: "Umbral OMS para considerar hipertensión grado 2." },
    diastolic: { enabled: true, value: 90, label: "Presión Arterial Diastólica (PAD)", unit: "mmHg", description: "Umbral OMS para considerar hipertensión grado 2." },
    smoking: { enabled: true, value: true, label: "Tabaquismo Activo", unit: "Sí/No", description: "Consumo de tabaco activo en el último año." },
    diabetes: { enabled: true, value: true, label: "Diagnóstico de Diabetes", unit: "Sí/No", description: "Presencia confirmada de Diabetes Mellitus." },
    imc: { enabled: true, value: 30, label: "Índice de Masa Corporal (IMC)", unit: "kg/m²", description: "Límite para clasificación de Obesidad de la OMS." }
  },
  triggerLogic: {
    minConditions: 2,
    alertSeverity: "warning",
    doctorMessageTemplate: "El usuario ha cumplido con los criterios de monitoreo cardiovascular de la OMS. Se sugiere agendar un chequeo preventivo.",
    userActionTemplate: "Se ha detectado una combinación de factores de riesgo según las guías de la OMS. Tu médico de familia ha sido notificado para seguimiento."
  }
};

// ── 3. Programa metabólico por defecto (Hiperglicemia y Diabetes) ─────────────
export const DEFAULT_HYPERGLYCEMIA_PROGRAM = {
  id: "hyperglycemia_diabetes",
  name: "Programa Corporativo de Intervención de Hiperglicemia y Diabetes",
  version: "1.0",
  icon: "Activity",
  biomarkers: {
    glucemia: {
      label: "Glucemia Capilar",
      loinc: "1558-6",
      unit: "mg/dL",
      ranges: [
        { min: 0, max: 69, color: "red", label: "Hipoglucemia", jitai: "Toma 15g de carbohidratos simples (media taza de jugo o 3 tabletas de glucosa). Espera 15 min y vuelve a medir (Regla 15x15). Alerta enviada al Oficial de Salud (IHO)." },
        { min: 70, max: 130, color: "green", label: "Euglucemia en Ayunas", jitai: "Nivel de glucosa óptimo en ayunas. ¡Sigue así! Mantén tu hidratación y tu plan de alimentación habitual." },
        { min: 131, max: 180, color: "yellow", label: "Elevación Leve (Postprandial)", jitai: "Snack de movimiento activo: camina a paso ligero durante 10 minutos para facilitar la captación muscular de glucosa sin inyectar insulina." },
        { min: 181, max: 249, color: "orange", label: "Hiperglucemia", jitai: "Nivel elevado. Realiza una sesión de respiración profunda para modular el cortisol, evita alimentos procesados y mantente hidratado con agua. Notificación enviada al Médico General para ajuste." },
        { min: 250, max: 1000, color: "red", label: "Hiperglucemia Severa", jitai: "Alerta Crítica. Evita realizar ejercicio extenuante si te sientes mal, mantén hidratación abundante y comunícate con tu Oficial de Salud (IHO). Se iniciará el protocolo de descarte de cetoacidosis." }
      ]
    },
    hba1c: {
      label: "Hemoglobina Glicada (HbA1c)",
      loinc: "4548-4",
      unit: "%",
      ranges: [
        { min: 0, max: 5.6, color: "green", label: "Normal", jitai: "Nivel saludable de hemoglobina glicada." },
        { min: 5.7, max: 6.4, color: "yellow", label: "Prediabetes", jitai: "Rango de prediabetes. Se recomienda incrementar la actividad física aeróbica e inscribirse en el Forochat de Hábitos Saludables." },
        { min: 6.5, max: 20, color: "red", label: "Diabetes Mellitus", jitai: "Control médico requerido. Sigue el tratamiento farmacológico indicado por tu médico de familia." }
      ]
    },
    egfr: {
      label: "Tasa de Filtración Glomerular (eGFR)",
      loinc: "62238-1",
      unit: "mL/min/1.73m²",
      ranges: [
        { min: 90, max: 300, color: "green", label: "Normal / Función Renal Excelente", jitai: "Excelente filtración renal." },
        { min: 30, max: 89, color: "yellow", label: "Función Renal Disminuida Leve-Moderada", jitai: "Monitoreo preventivo requerido. Controla estrictamente tu Presión Arterial." },
        { min: 0, max: 29, color: "red", label: "Fallo Renal Moderado-Grave", jitai: "Alerta Crítica. Sugerencia de consulta prioritaria con Médico General (Nivel 3)." }
      ]
    },
    uacr: {
      label: "Relación Albúmina/Creatinina (uACR)",
      loinc: "13705-9",
      unit: "mg/g",
      ranges: [
        { min: 0, max: 29, color: "green", label: "Normal / Sin microalbuminuria", jitai: "Sin daño de barrera glomerular." },
        { min: 30, max: 300, color: "yellow", label: "Microalbuminuria moderada", jitai: "Signo temprano de daño renal. Requiere control estricto de glucemia y valoración de IECA/ARA2." },
        { min: 301, max: 10000, color: "red", label: "Macroalbuminuria severa", jitai: "Alerta Roja. Daño glomerular significativo. Sugerencia de consulta con Médico General (Nivel 3)." }
      ]
    },
    homair: {
      label: "Índice HOMA-IR",
      loinc: "98380-2",
      unit: "{ratio}",
      ranges: [
        { min: 0, max: 1.9, color: "green", label: "Sensibilidad a la insulina óptima", jitai: "Excelente balance de insulina y glucemia." },
        { min: 2.0, max: 2.9, color: "yellow", label: "Resistencia moderada a la insulina", jitai: "Se sugiere restringir carbohidratos refinados y priorizar entrenamientos de fuerza de resistencia." },
        { min: 3.0, max: 50, color: "red", label: "Resistencia severa a la insulina", jitai: "Nivel de alta resistencia metabólica. Requiere manejo conjunto por Coach de Salud y Médico General." }
      ]
    },
    fib4: {
      label: "Índice FIB-4",
      loinc: "94344-9",
      unit: "{score}",
      ranges: [
        { min: 0, max: 1.29, color: "green", label: "Bajo riesgo de fibrosis hepática", jitai: "Tejido hepático saludable." },
        { min: 1.30, max: 2.67, color: "yellow", label: "Riesgo indeterminado de fibrosis", jitai: "Se sugiere monitorear enzimas hepáticas (AST/ALT) y evaluar hábitos de consumo de alcohol y grasas saturadas." },
        { min: 2.68, max: 20, color: "red", label: "Alto riesgo de fibrosis avanzada", jitai: "Alerta Roja. Daño hepático significativo. Sugerencia de consulta con Médico General (Nivel 3)." }
      ]
    }

  },
  rules: [
    { id: "r1", condition: "glucemia >= 250", targetLevel: 3, description: "Glucosa crítica con riesgo de cetoacidosis" },
    { id: "r2", condition: "egfr < 30", targetLevel: 3, description: "Daño renal moderado a severo" },
    { id: "r3", condition: "uacr > 300", targetLevel: 3, description: "Macroalbuminuria severa" },
    { id: "r4", condition: "fib4 > 1.3", targetLevel: 3, description: "Elevada fibrosis hepática" },
    { id: "r5", condition: "glucemia >= 180 && glucemia < 250", targetLevel: 2, description: "Hiperglicemia establecida" },
    { id: "r6", condition: "hba1c >= 6.5", targetLevel: 2, description: "Diabetes Mellitus confirmada por laboratorio" },
    { id: "r7", condition: "glucemia >= 100 && glucemia < 180", targetLevel: 1, description: "Alteración de glucosa en ayunas / prediabetes" },
    { id: "r8", condition: "hba1c >= 5.7 && hba1c < 6.5", targetLevel: 1, description: "Prediabetes por hemoglobina glicada" }
  ]
};

// ── 4. Cargar Umbrales Basales ────────────────────────────────────────────────
export async function loadThresholds() {
  try {
    const snap = await getDoc(doc(db, "config", "alertThresholds"));
    if (snap.exists()) {
      const stored = snap.data() || {};
      // Sanear escala Baevsky si es vieja
      if (stored.stressIndex && typeof stored.stressIndex.danger === "number" && stored.stressIndex.danger <= 100) {
        delete stored.stressIndex;
      }
      return { ...DEFAULT_THRESHOLDS, ...stored };
    }
  } catch (e) {
    console.error("Error al recuperar alertThresholds:", e);
  }
  return DEFAULT_THRESHOLDS;
}

export async function saveThresholds(thresholds) {
  await setDoc(doc(db, "config", "alertThresholds"), thresholds, { merge: true });
}

// ── 5. Cargar Configuración OMS ───────────────────────────────────────────────
export async function loadOmsConfig() {
  try {
    const snap = await getDoc(doc(db, "config", "omsQuestionnaire"));
    if (snap.exists() && snap.data().rules) {
      return {
        rules: snap.data().rules,
        triggerLogic: snap.data().triggerLogic
      };
    }
  } catch (e) {
    console.error("Error al recuperar omsQuestionnaire:", e);
  }
  return DEFAULT_OMS_THRESHOLDS;
}

export async function saveOmsConfig(config) {
  await setDoc(doc(db, "config", "omsQuestionnaire"), {
    rules: config.rules,
    triggerLogic: config.triggerLogic
  }, { merge: true });
}

// ── 6. Cargar Programas Clínicos (Multi-Patología) ───────────────────────────
export async function loadPrograms() {
  try {
    const colRef = collection(db, "config", "programs", "items");
    const snap = await getDocs(colRef);
    if (!snap.empty) {
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    }
    
    // Si la colección está vacía, precargar el programa de hiperglicemia por defecto
    const defaultProg = DEFAULT_HYPERGLYCEMIA_PROGRAM;
    await setDoc(doc(db, "config", "programs", "items", defaultProg.id), defaultProg);
    return [defaultProg];
  } catch (e) {
    console.error("Error al recuperar programas de Firestore:", e);
    return [DEFAULT_HYPERGLYCEMIA_PROGRAM];
  }
}

export async function saveProgram(programId, data) {
  if (!programId) return;
  await setDoc(doc(db, "config", "programs", "items", programId), data, { merge: true });
}

export async function deleteProgram(programId) {
  if (!programId) return;
  await deleteDoc(doc(db, "config", "programs", "items", programId));
}
